/* eslint-disable no-unused-vars */
import React, { Suspense, lazy } from 'react';
import {
  Navigate, useRoutes, useLocation,
} from 'react-router-dom';
import { useApp } from './utils/AppContext';
import { usesTagsFeature, isPlatformSuperAdmin } from './utils/orgFeatures';
import { FEATURES } from './config';

// import SimpleLayout from './layouts/simple';
// import SignUp from './sections/auth/signUp';
// import VerifyCode from './sections/auth/verify';
// import RequestCode from './sections/auth/requestCode';
// import ForgotPassword from './sections/auth/forgotPassword';
// import ChangePassword from './sections/auth/changePassword';

import AppLayout from './layouts/dashboard';
import WsProvider from './utils/WsContext';
import { element } from 'prop-types';
import RS485ConfigPage from './pages/RS485ConfigPage';
import { useAuth } from './utils/AuthContext';
import API from './utils/apiConfig';
// import OfflineFallback from './pages/OfflineFallback';

const Assets = lazy(() => import('./pages/assets'));
const Shipments = lazy(() => import('./pages/shipments'));
const PastShipmentsPage = lazy(() => import('./pages/shipments/PastShipmentsPage'));
const UserPage = lazy(() => import('./pages/UserPage'));
const SignIn = lazy(() => import('./sections/auth/signIn'));
const Page404 = lazy(() => import('./pages/Page404'));
const DevicesPage = lazy(() => import('./pages/DevicesPage'));
const DeviceCardsPage = lazy(() => import('./pages/DeviceCardsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const OrganizationPage = lazy(() => import('./pages/OrganizationPage'));
const PeriodicReportsPage = lazy(() => import('./pages/PeriodicReportsPage'));
const LitePageV1 = lazy(() => import('./pages/lite/LitePageV1'));
const LitePage = lazy(() => import('./pages/lite/LitePage'));
const GoMiniPage = lazy(() => import('./pages/gomini/GoMiniPage'));
const USBPage = lazy(() => import('./pages/usb/USBPage'));
const CDCPage = lazy(() => import('./pages/cdc/CDCPage'));
const CAPAsystem = lazy(() => import('./pages/CAPAsystem'));
const HootersPage = lazy(() => import('./pages/HootersPage'));
const OrgSelectionPage = lazy(() => import('./pages/OrgSelectionPage'));
const DistributorAccessPage = lazy(() => import('./pages/DistributorAccessPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const SharingPage = lazy(() => import('./pages/SharingPage'));
const DownloadsPage = lazy(() => import('./pages/DownloadsPage'));
const AlertRedirect = lazy(() => import('./pages/AlertRedirect'));
const DataCorrectionPage = lazy(() => import('./pages/DataCorrectionPage'));
const TagManagementPage = lazy(() => import('./pages/TagManagementPage'));
const FerreroOverviewPage = lazy(() => import('./pages/FerreroOverviewPage'));

const sus = (comp) => (
  <Suspense fallback={<div />}>
    {comp}
  </Suspense>
);

// Data-correction access is no longer tied to the Superuser group. The server governs it via a Redis
// allowlist (super-admin Surya in any org, or an allowlisted { email, orgId } pair in its own org).
// This guard asks the server (GET /v1/packets-correction/access) and shows the page or a 404. It is
// cosmetic — every correction endpoint re-checks the same allowlist server-side.
const DataCorrectionGuard = () => {
  const [status, setStatus] = React.useState('loading');
  React.useEffect(() => {
    let active = true;
    API.get('/v1/packets-correction/access')
      .then((r) => { if (active) setStatus(r?.data?.allowed ? 'allowed' : 'denied'); })
      .catch(() => { if (active) setStatus('denied'); });
    return () => { active = false; };
  }, []);
  if (status === 'loading') return <div />;
  return status === 'allowed' ? sus(<DataCorrectionPage />) : sus(<Page404 />);
};

// ----------------------------------------------------------------------

const RedirectToOrgs = () => {
  const { pathname, search } = useLocation();
  const redirectPath = `${pathname}${search || ''}`;
  return <Navigate to={`/organizations${redirectPath}`} />;
};

export default function Router() {
  const {
    id: orgId, assetsConfig, settings,
  } = useApp();
  const { sessionInfo } = useAuth();
  const isSuperUser = isPlatformSuperAdmin(sessionInfo?.idToken?.payload?.email) || (sessionInfo?.idToken?.payload?.['cognito:groups']?.some((g) => g.startsWith(orgId) && g.endsWith('Superuser')) ?? false);

  if (!orgId) {
    return useRoutes([
      { path: '/distributor-access', element: sus(<DistributorAccessPage />) },
      { path: '/organizations/al/:encodedPath', element: sus(<AlertRedirect />) },
      { path: '/organizations/*', element: sus(<OrgSelectionPage />) },
      { path: '/al/:encodedPath', element: sus(<AlertRedirect />) },
      { path: '*', element: <RedirectToOrgs /> },
    ]);
  }

  const routes = [
    { path: '/distributor-access', element: sus(<DistributorAccessPage />) },
    { path: '/organizations/*', element: sus(<OrgSelectionPage />) },
    {
      path: '/',
      element: sus(<AppLayout />),
      children: [
        { path: 'devices', element: sus(<DevicesPage />) },
        { path: 'devices/cards', element: sus(<DeviceCardsPage />) },
        { path: 'loggers', element: sus(<DevicesPage />) },
        { path: 'capa', element: sus(<CAPAsystem />) },
        { path: 'hooters', element: sus(<HootersPage />) },
        // Not merely hidden: unavailable. See FEATURES in config.js — the box must not hold
        // Cognito's pool-wide admin permissions. Without this a bookmark or a typed URL still
        // opened the page, which then showed the API's refusal as a full-width red error.
        { path: 'usermanagement', element: FEATURES.users ? sus(<UserPage />) : sus(<Page404 />) },
        { path: 'lite/', element: sus(<LitePage />) },
        { path: 'gomini/', element: sus(<GoMiniPage />) },
        { path: 'lite/v2', element: sus(<LitePage />) },
        { path: 'lite/v1', element: sus(<LitePageV1 />) },
        { path: 'usb', element: sus(<USBPage />) },
        { path: 'cdc', element: sus(<CDCPage />) },
        { path: 'rs485-config', element: sus(<RS485ConfigPage />) },
        { path: 'settings/profile', element: sus(<ProfilePage />) },
        { path: 'settings/integrations', element: sus(<IntegrationsPage />) },
        { path: 'settings/organization', element: sus(<OrganizationPage />) },
        { path: 'settings/fields', element: usesTagsFeature(orgId) ? sus(<TagManagementPage />) : sus(<Page404 />) },
        { path: 'downloads', element: sus(<DownloadsPage />) },
        { path: 'signin/*', element: <Navigate to="/" /> },
        { path: 'data-correction', element: sus(<DataCorrectionGuard />) },
        { path: 'distributor-overview', element: sus(<FerreroOverviewPage />) },
        { path: 'share/:linkId', element: sus(<SharingPage />) },
        { path: 'track/:deviceId', element: sus(<SharingPage />) },
        { path: 'al/:encodedPath', element: sus(<AlertRedirect />) },
        { path: 'auth/*', element: <Navigate to="/" /> },
        { path: '*', element: sus(<Page404 />) },
      ],
    },
  ];

  let landingRoute = settings?.assetPriority?.[0] ? `${settings.assetPriority[0]}s` : '';

  if (assetsConfig?.asset) {
    if (!landingRoute) landingRoute = '/assets';
    routes[routes.length - 1].children.push(
      { path: '/assets', element: sus(<Assets />) },
      { path: '/anchor/assets', element: sus(<Assets />) },
      ...((!settings.hidePeriodicReports || isSuperUser) ? [{ path: 'settings/periodic-reports', element: sus(<PeriodicReportsPage />) }] : []),
      // { path: '/anchor/loggers', element: sus(<DevicesPage solution="anchor" />) },
    );
  }
  if (assetsConfig?.shipment) {
    if (!landingRoute) landingRoute = '/shipments';
    routes[routes.length - 1].children.push(
      { path: '/shipments', element: sus(<Shipments />) },
      { path: '/shipments/past', element: sus(<PastShipmentsPage />) },
      { path: '/shipments/stops', element: sus(<DevicesPage />) },
      { path: '/go/shipments', element: sus(<Shipments />) },
      { path: '/go/shipments/past', element: sus(<PastShipmentsPage />) },
      // { path: '/go/loggers', element: sus(<DevicesPage solution="go" />) },
    );
  }

  routes[routes.length - 1].children.unshift({ path: '/', element: <Navigate to={landingRoute} />, index: true });

  routes.push();

  return (
    <WsProvider>
      {useRoutes(routes)}
    </WsProvider>
  );
}

const RedirectToSignIn = () => {
  const { pathname, search } = useLocation();
  const redirectPath = `${pathname}${search || ''}`;
  console.log('Redirecting to sign-in:', redirectPath);
  if (pathname.includes('callback')) return <Navigate to="/" />;
  return <Navigate to={`/signin${redirectPath}`} />;
};

export const SignInRoute = () => useRoutes(
  [
    { path: 'signin/*', element: sus(<SignIn />) },
    // { path: '/signup', element: <SignUp /> },
    // { path: '/verify', element: <VerifyCode /> },
    // { path: '/requestcode', element: <RequestCode /> },
    // { path: '/forgotpassword', element: <ForgotPassword /> },
    {
      path: '*',
      element: sus(<AppLayout />),
      children: [
        { path: 'lite/', element: sus(<LitePage />) },
        { path: 'gomini/', element: sus(<GoMiniPage />) },
        { path: 'lite/v2', element: sus(<LitePage />) },
        { path: 'lite/v1', element: sus(<LitePageV1 />) },
        { path: 'usb', element: sus(<USBPage />) },
        { path: 'cdc', element: sus(<CDCPage />) },
        { path: 'share/:linkId', element: sus(<SharingPage />) },
        { path: 'track/:deviceId', element: sus(<SharingPage />) },
        { path: 'al/:encodedPath', element: sus(<AlertRedirect />) },
        { path: '*', element: <RedirectToSignIn /> },
      ],
    },
  ],
);

export const IframeRoute = () => useRoutes(
  [
    { path: 'devices/:deviceId', element: sus(<DevicesPage iframe />) },
    { path: 'track/:deviceId', element: sus(<SharingPage iframe />) },
    { path: '*', element: <Page404 /> },
  ],
);
